import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Chart from '../../chart';

import VolSeriesChip from '../../../series-chip/vol_series_chip';
import { lineChartColors } from '../../../../theme/colors';

import { RiskFiltersContext } from '../../../../providers/risk-management/risk-filters-provider';
import { RiskManagementContext } from '../../../../providers/risk-management/risk-management-provider';
import Utils from '../../../../utils/immersive_utils';
import chartTheme from '../../chart/theme';
import './styles.scss';

type RiskMetricContentProps = {
  id: string;
  minIndex: number;
  maxIndex: number;
};

type SeriesChip = {
  key: string;
  label: string;
  color: string;
  visible: boolean;
};

const RiskMetricContent = ({ id, minIndex, maxIndex }: RiskMetricContentProps) => {
  const { dataGraph, spotData } = useContext(RiskManagementContext);
  const { filters } = useContext(RiskFiltersContext);
  const { timeList } = filters || ({ timeList: [] } as any);

  const [items, setItems] = useState<SeriesChip[]>([]);
  const [xAxisData, setXAxisData] = useState<string[]>([]);

  useEffect(() => {
    const nextItems: SeriesChip[] = (timeList || [])
      .filter((i: any) => i?.isVisible)
      .map((item: any, index: number) => ({
        key: item.value,
        label: item.label,
        color: lineChartColors[index % lineChartColors.length],
        visible: index === 0,
      }));
    setItems(nextItems);
  }, [timeList]);

  useEffect(() => {
    const hasValidRange = minIndex >= 0 && maxIndex >= minIndex;
    if (!hasValidRange) return;

    if (spotData?.values?.length) {
      const sliced = spotData.values.slice(minIndex, maxIndex + 1).map((v: any) => `${v}`);
      setXAxisData(sliced);
    } else {
      setXAxisData([]);
    }
  }, [spotData, minIndex, maxIndex]);

  const metricId = useMemo(() => {
    const m = String(id || '');
    const pref = 'risk-graph-';
    return m.startsWith(pref) ? m.slice(pref.length) : m;
  }, [id]);

  const series = useMemo(() => {
    const hasValidRange = minIndex >= 0 && maxIndex >= minIndex;
    if (!hasValidRange) return [] as any[];

    if (!Array.isArray(dataGraph) || dataGraph.length === 0) return [] as any[];

    const visibleItems = items.filter((i) => i.visible);
    if (!visibleItems.length) return [] as any[];

    const built: any[] = [];

    const normalizeHorizon = (val: any) => {
      const s = String(val ?? '');
      const mT = s.match(/^T\+(\d+)$/i);
      if (mT) return `${mT[1]}D`;
      const mD = s.match(/^(\d+)D$/i);
      if (mD) return `${mD[1]}D`;
      return s;
    };

    visibleItems.forEach((it) => {
      const keyNorm = normalizeHorizon(it.key);
      const labelNorm = normalizeHorizon(it.label);
      const dataEntry = dataGraph.find((d: any) => {
        const h = normalizeHorizon(d?.['horizon_date']);
        return h === keyNorm || h === labelNorm;
      });
      const dataByHorizon = dataEntry?.data;
      const itemData = dataByHorizon?.find?.((i: any) => i?.id === metricId);
      if (
        !itemData ||
        !Array.isArray(itemData.values) ||
        itemData.values.length === 0 ||
        itemData.values.every((v: any) => v == null)
      )
        return;

      const values = itemData.values
        .slice(minIndex, maxIndex + 1)
        .map((n: any) => (n == null ? null : Number(n)));

      built.push({
        name: it.label,
        type: 'line',
        smooth: true,
        animation: true,
        color: it.color,
        showAllSymbol: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 1.5 },
        z: 2,
        data: values,
      });
    });

    return built;
  }, [dataGraph, items, metricId, minIndex, maxIndex]);

  const handleChipClick = useCallback((key: string) => {
    setItems((prevItems) => {
      const visibleItems = prevItems.filter((item) => item.visible);
      if (visibleItems.length === 1 && visibleItems[0].key === key) {
        return prevItems;
      }
      return prevItems.map((item) => ({
        ...item,
        visible: item.key === key ? !item.visible : item.visible,
      }));
    });
  }, []);

  const resetItems = useCallback(() => {
    setItems((prev) => prev.map((item) => ({ ...item, visible: true })));
  }, []);

  const itemsVisible = items.filter((i) => i.visible);
  const allItemsVisible = itemsVisible.length === items.length;

  const renderChips = () => (
    <div className="risk-graph-chips-container">
      <VolSeriesChip
        series={{ label: 'All', color: 'transparent' } as any}
        onClick={resetItems}
        classnames="risk-metric-all-chip"
        disabled={allItemsVisible}
      />
      <div className="chips-divider" />
      {items?.map((item) => (
        <div key={item.key} className="risk-graph-chips">
          <VolSeriesChip
            series={{ label: item.label, color: item.color } as any}
            onClick={() => handleChipClick(item.key)}
            removable={false}
            disabled={!item.visible}
          />
        </div>
      ))}
    </div>
  );

  const xAxis = useMemo(
    () => ({
      type: 'category',
      data: xAxisData,
      axisLabel: {
        formatter: (v: any) => Utils.formatNumber(v),
        rotate: 0,
        hideOverlap: true,
        interval: 'auto' as any,
        align: 'left',
        showMinLabel: true,
        showMaxLabel: false,
        margin: 16,
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
        interval: 0,
      },
      splitLine: {
        show: true,
        interval: 0,
        lineStyle: { color: (chartTheme as any)?.colors?.splitLine },
      },
      boundaryGap: false,
    }),
    [xAxisData],
  );

  const yAxis = useMemo(
    () => ({
      type: 'value',
      position: 'right',
      axisPointer: { label: { margin: 10 } },
      axisLabel: {
        formatter: (value: any) => Utils.formatNumber(Number(value)),
        inside: false,
        align: 'left',
        margin: 10,
      },
      min: (v: any) => {
        const min = Number(v?.min ?? 0);
        const max = Number(v?.max ?? 0);
        if (min === max) {
          const pad = Math.max(1, Math.abs(min) * 0.05);
          return min - pad;
        }
        return min;
      },
      max: (v: any) => {
        const min = Number(v?.min ?? 0);
        const max = Number(v?.max ?? 0);
        if (min === max) {
          const pad = Math.max(1, Math.abs(max) * 0.05);
          return max + pad;
        }
        return max;
      },
    }),
    [],
  );

  return (
    <div className="risk-metric-content">
      <div className="risk-graph-chips">{renderChips()}</div>
      <Chart
        className="risk-metric-chart"
        series={series}
        xAxis={xAxis as any}
        yAxis={yAxis as any}
        merge={false}
        useBoundaryGap={false}
        legend={{ show: false }}
        grid={{ left: 10, right: 10, top: 15, bottom: 0 }}
      />
    </div>
  );
};

export default RiskMetricContent;
